import React from "react";
import { api, formatApiError } from "@/lib/api";
import { toast } from "sonner";

export function PhotoFilterBar({ events, eventId, groupId, onEvent, onGroup, selectMode, onToggleSelect, shownCount, onSelectAll, onClear, selectedCount }) {
  const ev = events.find((e) => e.id === eventId);
  return (
    <div className="flex flex-wrap items-center gap-3 border border-white/10 bg-[#0a0a0a] p-4 mb-6" data-testid="photo-filter-bar">
      <select value={eventId} onChange={(e) => onEvent(e.target.value)} className="input-minimal bg-transparent text-sm py-2 !w-auto min-w-[200px]" data-testid="filter-event-select">
        <option value="" className="bg-black">All events</option>
        {events.map((e) => <option key={e.id} value={e.id} className="bg-black">{e.date} · {e.track} · {e.name}</option>)}
      </select>
      <select value={groupId} onChange={(e) => onGroup(e.target.value)} disabled={!eventId} className="input-minimal bg-transparent text-sm py-2 !w-auto min-w-[160px] disabled:opacity-40" data-testid="filter-group-select">
        <option value="" className="bg-black">All classes</option>
        {(ev?.groups || []).map((g) => <option key={g.id} value={g.id} className="bg-black">{g.name}</option>)}
      </select>
      <div className="ml-auto flex items-center gap-3">
        {selectMode && (
          <>
            <button onClick={onSelectAll} className="text-xs uppercase tracking-[0.2em] text-white/70 hover:text-[var(--pm-orange)]" data-testid="select-all-shown">Select all shown ({shownCount})</button>
            {selectedCount > 0 && <button onClick={onClear} className="text-xs uppercase tracking-[0.2em] text-white/50 hover:text-white" data-testid="select-clear">Clear</button>}
          </>
        )}
        <button
          onClick={onToggleSelect}
          className={`border px-4 py-2 text-xs uppercase tracking-[0.25em] font-semibold transition-colors ${selectMode ? "border-[var(--pm-orange)] bg-[var(--pm-orange)] text-black" : "border-white/25 hover:border-[var(--pm-orange)]"}`}
          data-testid="bulk-select-toggle"
        >
          {selectMode ? "Done selecting" : "Bulk edit"}
        </button>
      </div>
    </div>
  );
}

export function BulkEditBar({ selectedIds, events, onDone, onClear }) {
  const [price, setPrice] = React.useState("");
  const [currency, setCurrency] = React.useState("");
  const [description, setDescription] = React.useState("");
  const [touchDesc, setTouchDesc] = React.useState(false);
  const [moveTo, setMoveTo] = React.useState("");
  const [busy, setBusy] = React.useState(false);
  const count = selectedIds.length;
  if (count === 0) return null;

  const hasChange = price !== "" || currency !== "" || touchDesc || moveTo !== "";

  const apply = async () => {
    const payload = { image_ids: selectedIds };
    if (price !== "") payload.price = Number(price);
    if (currency) payload.currency = currency;
    if (touchDesc) payload.description = description;
    if (moveTo) payload.move_to_group_id = moveTo;
    const summary = [
      price !== "" && `price → ${price}`,
      currency && `currency → ${currency.toUpperCase()}`,
      touchDesc && "description",
      moveTo && "move to another class",
    ].filter(Boolean).join(", ");
    if (!window.confirm(`Apply to ${count} photo${count === 1 ? "" : "s"}: ${summary}?`)) return;
    setBusy(true);
    try {
      const r = await api.post("/admin/images/bulk-edit", payload);
      toast.success(`Updated ${r.data.modified} of ${r.data.matched} photos`);
      setPrice(""); setCurrency(""); setDescription(""); setTouchDesc(false); setMoveTo("");
      onDone();
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="fixed bottom-0 left-0 right-0 z-40 border-t-2 border-[var(--pm-orange)] bg-[#050505]/95 backdrop-blur-md" data-testid="bulk-edit-bar">
      <div className="max-w-7xl mx-auto px-6 py-4 flex flex-wrap items-end gap-4">
        <div className="mr-2">
          <p className="text-xs uppercase tracking-[0.3em] pm-orange font-semibold" data-testid="bulk-selected-count">{count} selected</p>
          <button onClick={onClear} className="text-[10px] uppercase tracking-[0.15em] text-white/50 hover:text-white mt-1" data-testid="bulk-clear">Clear selection</button>
        </div>
        <Field label="New price">
          <input type="number" min="0" step="0.01" value={price} onChange={(e) => setPrice(e.target.value)} placeholder="Leave blank to keep" className="input-minimal text-sm py-1.5 !w-36" data-testid="bulk-price-input" />
        </Field>
        <Field label="Currency">
          <select value={currency} onChange={(e) => setCurrency(e.target.value)} className="input-minimal bg-transparent text-sm py-1.5 !w-24" data-testid="bulk-currency-select">
            <option value="" className="bg-black">Keep</option>
            {["gbp", "eur", "usd"].map((c) => <option key={c} value={c} className="bg-black">{c.toUpperCase()}</option>)}
          </select>
        </Field>
        <Field label="Move to class">
          <select value={moveTo} onChange={(e) => setMoveTo(e.target.value)} className="input-minimal bg-transparent text-sm py-1.5 !w-auto min-w-[200px]" data-testid="bulk-move-select">
            <option value="" className="bg-black">Keep</option>
            {events.map((ev) => (
              <optgroup key={ev.id} label={`${ev.date} · ${ev.track}`} className="bg-black">
                {(ev.groups || []).map((g) => <option key={g.id} value={g.id} className="bg-black">{g.name}</option>)}
              </optgroup>
            ))}
          </select>
        </Field>
        <Field label="Description">
          <input value={description} onChange={(e) => { setDescription(e.target.value); setTouchDesc(true); }} placeholder="Leave blank to keep" className="input-minimal text-sm py-1.5 !w-56" data-testid="bulk-description-input" />
        </Field>
        <button
          onClick={apply}
          disabled={!hasChange || busy}
          className="ml-auto bg-[var(--pm-orange)] text-black px-6 py-2.5 text-xs uppercase tracking-[0.25em] font-semibold hover:bg-white disabled:opacity-40 transition-colors"
          data-testid="bulk-apply-button"
        >
          {busy ? "Applying…" : `Apply to ${count}`}
        </button>
      </div>
    </div>
  );
}

function Field({ label, children }) {
  return (
    <label className="flex flex-col gap-1">
      <span className="text-[10px] uppercase tracking-[0.2em] text-white/40">{label}</span>
      {children}
    </label>
  );
}
